import {CommonModule} from '@angular/common';
import {Component, ViewChild} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {Router} from '@angular/router';
import {AmountPipe, EmDatePipe, EmTranslatePipe, TranslationService} from '@emma-labs/shared/angular';
import {
  Agent,
  DerivedUnderwritingProfileStatus,
  Dropdown,
  getDerivedUnderwritingProfileStatus,
  getMonthlyPrice,
  getTotalCoverage,
  MinifiedUnderwritingProfile,
  UNDERWRITING_PROFILE_STATUS_OPTIONS,
} from '@emma-labs/shared/typescript';
import {BadgeModule} from 'primeng/badge';
import {ButtonModule} from 'primeng/button';
import {CardModule} from 'primeng/card';
import {DropdownModule} from 'primeng/dropdown';
import {InputTextModule} from 'primeng/inputtext';
import {MultiSelectModule} from 'primeng/multiselect';
import {SkeletonModule} from 'primeng/skeleton';
import {Table, TableModule} from 'primeng/table';
import {forkJoin, Observable, of, switchMap} from 'rxjs';
import {first, map} from 'rxjs/operators';
import {AgentService} from '../../services/agent.service';
import {AuthService} from '../../services/auth.service';
import {UnderwritingProfileService} from '../../services/underwriting/underwriting-profile.service';
import {MainComponent} from '../main/main.component';
import {TableEntry} from './table-entry';

@Component({
  selector: 'em-underwriting-search-page',
  templateUrl: './underwriting-profiles-page.component.html',
  imports: [
    MainComponent,
    CommonModule,
    CardModule,
    TableModule,
    InputTextModule,
    ButtonModule,
    BadgeModule,
    SkeletonModule,
    DropdownModule,
    MultiSelectModule,
    FormsModule,
    EmDatePipe,
    AmountPipe,
    EmTranslatePipe,
  ],
})
export class UnderwritingProfilesPageComponent {
  @ViewChild('underwritingProfilesTables') underwritingProfilesTable!: Table;

  tableEntries$: Observable<TableEntry[]> = new Observable<TableEntry[]>();
  agent?: Agent;
  agentInChargeOptions: Dropdown<string>[] = [];
  noAgentInChargeStr: string;
  readonly defaultRowsPerPage = 10;
  readonly status = DerivedUnderwritingProfileStatus;
  readonly statusOptions = UNDERWRITING_PROFILE_STATUS_OPTIONS;

  constructor(
    private readonly router: Router,
    private readonly underwritingProfileService: UnderwritingProfileService,
    private readonly translate: TranslationService,
    private readonly agentService: AgentService,
    private readonly authService: AuthService,
  ) {
    this.noAgentInChargeStr = this.translate.currentLanguageIsFr() ? 'Aucun' : 'None';

    this.authService.currentAgentSessionOrAuthError
      .pipe(first())
      .subscribe((session) => (this.agent = session.agent));

    this.tableEntries$ = this.underwritingProfileService.getAll().pipe(
      first(),
      switchMap((underwritingProfiles) => {
        const agentIds = underwritingProfiles.filter((up) => up.underwriterId).map((up) => up.underwriterId!);

        return forkJoin([this.agentService.getAgentsFromIds(agentIds), of(underwritingProfiles)]);
      }),
      map(([agents, underwritingProfiles]) => {
        this.agentInChargeOptions = [
          {
            name: this.noAgentInChargeStr,
            value: this.noAgentInChargeStr,
          },
          ...agents.map((agent) => ({
            name: agent.fullName,
            value: agent.fullName,
          })),
        ];

        return this.mapToTableEntries(underwritingProfiles, agents);
      }),
    );
  }

  open(id: string): void {
    this.router.navigate(['/', 'dashboard', 'underwriting', btoa(id)]);
  }

  applyFilterGlobal($event: Event): void {
    const value = ($event.target as HTMLInputElement).value.trim();
    this.underwritingProfilesTable.filterGlobal(value, 'contains');
  }

  private mapToTableEntries(
    underwritingProfiles: MinifiedUnderwritingProfile[],
    agents: {id: string; insurerCode: string; fullName: string}[],
  ): TableEntry[] {
    return underwritingProfiles.map((underwritingProfile) => {
      const status = getDerivedUnderwritingProfileStatus(
        underwritingProfile.status,
        underwritingProfile.archivedProposal.healthAnswersRevisions,
        underwritingProfile.extraRequirementsFile,
      );

      const agentInCharge =
        agents.find((agent) => agent.id === underwritingProfile.underwriterId)?.fullName ??
        this.noAgentInChargeStr;
      const clientFullName = `${underwritingProfile.archivedProposal.insuree.firstName} ${underwritingProfile.archivedProposal.insuree.lastName}`;

      return {
        id: underwritingProfile.id,
        agentInCharge,
        clientFullName,
        status: {text: this.translate.getTranslation(status)!, value: status},
        submissionDate: underwritingProfile.archivedProposal.submittedAt,
        policyNumber: underwritingProfile.policyNumber,
        coverage: getTotalCoverage(underwritingProfile.archivedProposal.protection!.personInsurances),
        premium: getMonthlyPrice(underwritingProfile.archivedProposal.protection!.personInsurances),
      };
    });
  }
}
